import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Layout from '../components/ui/Layout';
import UserProfile from 'features/auth/components/UserProfile';
import PasswordChangeForm from 'features/auth/components/PasswordChangeForm';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import ProfilePhotoUpload from 'features/auth/components/ProfilePhotoUpload';

const Profile = () => {
  const { data, isPending, error } = useUserProfile();
  const user = data?.data;

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['profile', 'photo', 'password'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  if (isPending) {
    return (
      <Layout>
        <div className='flex items-center justify-center min-h-screen'>
          <div className='text-center'>
            <div className='w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4'></div>
            <p className='text-muted-foreground'>Loading profile...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className='flex items-center justify-center min-h-screen'>
          <div className='text-center'>
            <Icon
              name='AlertCircle'
              size={48}
              className='text-destructive mx-auto mb-4'
            />
            <p className='text-destructive'>
              Failed to load profile. Please try again.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='p-4 md:p-6 max-w-5xl mx-auto'>
        {/* Header */}
        <div className='mb-6'>
          <h1 className='text-2xl md:text-3xl font-bold text-foreground mb-2'>
            Profile Settings
          </h1>
          <p className='text-sm md:text-base text-muted-foreground'>
            Manage your account settings and personal information
          </p>
        </div>

        {/* Profile Header Card */}
        <div className='bg-card border border-border rounded-lg p-4 md:p-6 mb-6 shadow-sm'>
          <div className='flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-4'>
            <div className='relative w-20 h-20 md:w-24 md:h-24 flex-shrink-0'>
              {user?.profilePhoto ? (
                <img
                  src={user.profilePhoto}
                  alt='Profile'
                  className='w-full h-full rounded-full object-cover'
                />
              ) : (
                <div className='w-full h-full bg-primary rounded-full flex items-center justify-center'>
                  <Icon name='User' size={40} color='white' />
                </div>
              )}
            </div>
            <div className='flex-1 text-center sm:text-left'>
              <h2 className='text-xl md:text-2xl font-semibold text-foreground'>
                {user?.name || 'User'}
              </h2>
              <p className='text-sm md:text-base text-muted-foreground mt-1'>
                {user?.email}
              </p>
              <div className='flex items-center justify-center sm:justify-start space-x-2 mt-2'>
                <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize'>
                  {user?.role?.name || 'user'}
                </span>
                <div className='flex items-center space-x-1'>
                  <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                  <span className='text-xs text-muted-foreground capitalize'>
                    {user?.status || 'Active'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Account Info - Visible on larger screens */}
          <div className='mt-6 pt-6 border-t border-border hidden md:grid grid-cols-2 lg:grid-cols-3 gap-4'>
            <div>
              <p className='text-xs text-muted-foreground mb-1'>Phone Number</p>
              <p className='text-sm font-medium text-foreground'>
                {user?.phoneNumber || 'Not set'}
              </p>
            </div>
            <div>
              <p className='text-xs text-muted-foreground mb-1'>Member Since</p>
              <p className='text-sm font-medium text-foreground'>
                {user?.createdAt
                  ? new Date(user.createdAt).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
            {/* <div>
              <p className='text-xs text-muted-foreground mb-1'>Two-Factor Auth</p>
              <p className='text-sm font-medium text-foreground'>
                {user?.twoFactorAuth?.isEnabled ? (
                  <span className='text-green-600'>Enabled</span>
                ) : (
                  <span className='text-muted-foreground'>Disabled</span>
                )}
              </p>
            </div> */}
          </div>
        </div>

        {/* Tabs */}
        <div className='bg-card border border-border rounded-lg overflow-hidden shadow-sm'>
          <div className='border-b border-border'>
            <div className='flex overflow-x-auto'>
              <button
                onClick={() => handleTabChange('profile')}
                className={`px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'profile'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className='flex items-center space-x-2'>
                  <Icon name='User' size={18} />
                  <span>Profile Information</span>
                </div>
              </button>
              <button
                onClick={() => handleTabChange('photo')}
                className={`px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'photo'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className='flex items-center space-x-2'>
                  <Icon name='Camera' size={18} />
                  <span>Profile Photo</span>
                </div>
              </button>
              <button
                onClick={() => handleTabChange('password')}
                className={`px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'password'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className='flex items-center space-x-2'>
                  <Icon name='Lock' size={18} />
                  <span>Change Password</span>
                </div>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className='p-4 md:p-6'>
            {/* Profile Information Tab */}
            {activeTab === 'profile' && <UserProfile />}

            {/* Profile Photo Tab */}
            {activeTab === 'photo' && <ProfilePhotoUpload />}

            {/* Change Password Tab */}
            {activeTab === 'password' && <PasswordChangeForm />}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
